/**
 * Parallel Effect Class for Poker Reborn Effects Engine
 *
 * Container effect that plays child effects simultaneously.
 *
 * @see tasks/active/EFFECTS_ENGINE_IMPLEMENTATION_PLAN.md - Phase 2.2.6
 */

import { log } from '@/utils/log';
import type { Effect, EffectOptions } from './Effect';
import type { EffectResult } from './EffectTypes';
import { EffectType, EffectState, EffectPriority } from './EffectTypes';
import { BaseEffect } from './BaseEffect';

// =============================================================================
// PARALLEL EFFECT CLASS (P2.2.6)
// =============================================================================

/**
 * Plays child effects simultaneously.
 * Completes when ALL children complete (or any fails, depending on mode).
 *
 * Example:
 * ```
 * new ParallelEffect([
 *   new ChipMoveEffect(pot),
 *   new SoundEffect('chips'),
 *   new TableGlowEffect(),
 * ])
 * ```
 */
export class ParallelEffect extends BaseEffect {
  private readonly _effects: Effect[];
  private readonly _failFast: boolean;
  private _completedCount = 0;
  private _results: EffectResult[] = [];

  constructor(
    effects: Effect[],
    options: Omit<EffectOptions, 'duration'> & {
      /** If true, cancel all on first failure */
      failFast?: boolean | undefined;
    } = {}
  ) {
    // Duration is the max of all children
    const maxDuration = effects.length > 0
      ? Math.max(...effects.map((e) => e.duration))
      : 0;

    // Use the highest priority among children, or NORMAL
    const maxPriority = effects.length > 0
      ? Math.max(...effects.map((e) => e.priority))
      : EffectPriority.NORMAL;

    super('parallel', EffectType.PARALLEL, { type: 'global' }, {
      ...options,
      duration: maxDuration,
      priority: options.priority ?? maxPriority,
    });

    this._effects = effects;
    this._failFast = options.failFast ?? false;
  }

  /**
   * Get the child effects.
   */
  get effects(): ReadonlyArray<Effect> {
    return this._effects;
  }

  /**
   * Get count of completed children.
   */
  get completedCount(): number {
    return this._completedCount;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle Implementation
  // ---------------------------------------------------------------------------

  protected onPlay(): void {
    if (this._effects.length === 0) {
      this.complete();
      return;
    }

    log.effects.debug`ParallelEffect: playing ${this._effects.length} effects simultaneously`;

    // Start all effects
    for (const effect of this._effects) {
      void this.playChild(effect);
    }
  }

  protected onCancel(): void {
    // Cancel all children
    for (const effect of this._effects) {
      if (effect.cancellable && effect.state === EffectState.PLAYING) {
        effect.cancel();
      }
    }
  }

  protected override onPause(): void {
    for (const effect of this._effects) {
      if (effect.pausable && effect.state === EffectState.PLAYING) {
        effect.pause();
      }
    }
  }

  protected override onResume(): void {
    for (const effect of this._effects) {
      if (effect.state === EffectState.PAUSED) {
        effect.resume();
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Private Methods
  // ---------------------------------------------------------------------------

  private async playChild(effect: Effect): Promise<void> {
    try {
      const result = await effect.play();
      this._results.push(result);

      // Handle failure
      if (!result.success && this._failFast) {
        log.effects.debug`ParallelEffect: child failed, cancelling others`;
        this.cancel();
        return;
      }

      this.onChildComplete();
    } catch (error) {
      log.effects.error`ParallelEffect child error: ${error}`;

      if (this._failFast) {
        this.completeWithError(error as Error);
      } else {
        this.onChildComplete();
      }
    }
  }

  private onChildComplete(): void {
    this._completedCount++;

    // Update progress
    const progress = this._completedCount / this._effects.length;
    this.updateProgress(progress);

    // Check if all done
    if (this._completedCount >= this._effects.length) {
      // Check if any failed
      const anyFailed = this._results.some((r) => !r.success);
      if (anyFailed && !this._failFast) {
        log.effects.warning`ParallelEffect: some children failed`;
      }
      this.complete();
    }
  }

  // ---------------------------------------------------------------------------
  // Conflict Detection Override
  // ---------------------------------------------------------------------------

  override conflictsWith(other: Effect): boolean {
    // A parallel conflicts if any of its children conflict
    return this._effects.some((e) => e.conflictsWith(other));
  }
}

// =============================================================================
// FACTORY HELPER
// =============================================================================

/**
 * Create a parallel group of effects.
 */
export function createParallel(
  effects: Effect[],
  options: Omit<EffectOptions, 'duration'> & { failFast?: boolean | undefined } = {}
): ParallelEffect {
  return new ParallelEffect(effects, options);
}
